import api from '@/api/axios';
import type { BffApiResponse } from '@/types';

export interface AdminUserResponse {
  id: string;
  username: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  enabled: boolean;
  createdTimestamp: string;
  userRole: string;
  phone: string | null;
  idAco: string | null;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  groups: string[];
  registrationDate: string | null;
  score: number | null;
  adviser: string | null;
  commerceId: string | null;
  requestApproved: boolean | null;
}

export interface AdminUserListResponse {
  users: AdminUserResponse[];
  total: number;
  page: number;
  size: number;
}

export interface PreRegisterUserRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  userRole: 'operator' | 'comissionist' | 'adviser' | 'none';
  phone?: string;
  idAco?: string;
  groupName?: string;
  registrationDate?: string;
  score?: number;
  adviser?: string;
  commerceId?: string;
  requestApproved?: boolean;
}

export interface AdminUpdateUserRequest {
  firstName: string;
  lastName: string;
  email?: string;
  userRole?: 'operator' | 'comissionist' | 'adviser' | 'none';
  phone?: string;
  idAco?: string;
  emailVerified?: boolean;
  enabled?: boolean;
  registrationDate?: string;
  score?: number;
  adviser?: string;
  commerceId?: string;
  requestApproved?: boolean;
}

export interface AdminResetPasswordRequest {
  newPassword: string;
  temporary?: boolean;
}

export interface BulkUserStatusRequest {
  userIds: string[];
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
  email: string | null;
}

class AdminUsersService {
  private readonly basePath = '/admin/users';

  async listUsers(
    page: number = 0,
    size: number = 20,
    search?: string
  ): Promise<BffApiResponse<AdminUserListResponse>> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(size));
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await api.get<BffApiResponse<AdminUserListResponse>>(
      `${this.basePath}?${params.toString()}`
    );
    return response.data;
  }

  async getUserById(userId: string): Promise<BffApiResponse<AdminUserResponse>> {
    const response = await api.get<BffApiResponse<AdminUserResponse>>(
      `${this.basePath}/${userId}`
    );
    return response.data;
  }

  async preRegisterUser(
    data: PreRegisterUserRequest
  ): Promise<BffApiResponse<AdminUserResponse>> {
    const response = await api.post<BffApiResponse<AdminUserResponse>>(
      `${this.basePath}/pre-register`,
      data
    );
    return response.data;
  }

  async updateUser(
    userId: string,
    data: AdminUpdateUserRequest
  ): Promise<BffApiResponse<AdminUserResponse>> {
    const response = await api.put<BffApiResponse<AdminUserResponse>>(
      `${this.basePath}/${userId}`,
      data
    );
    return response.data;
  }

  async blockUser(userId: string): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/${userId}/block`
    );
    return response.data;
  }

  async unblockUser(userId: string): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/${userId}/unblock`
    );
    return response.data;
  }

  async bulkBlockUsers(userIds: string[]): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/bulk-block`,
      { userIds }
    );
    return response.data;
  }

  async bulkUnblockUsers(userIds: string[]): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/bulk-unblock`,
      { userIds }
    );
    return response.data;
  }

  async resetPassword(
    userId: string,
    data: AdminResetPasswordRequest
  ): Promise<BffApiResponse<null>> {
    const response = await api.put<BffApiResponse<null>>(
      `${this.basePath}/${userId}/reset-password`,
      data
    );
    return response.data;
  }

  async deleteUser(userId: string): Promise<BffApiResponse<null>> {
    const response = await api.delete<BffApiResponse<null>>(
      `${this.basePath}/${userId}`
    );
    return response.data;
  }

  async sendPasswordResetEmail(userId: string): Promise<BffApiResponse<null>> {
    const response = await api.post<BffApiResponse<null>>(
      `${this.basePath}/${userId}/send-reset-email`
    );
    return response.data;
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<BffApiResponse<null>> {
    const response = await api.post<BffApiResponse<null>>(
      `/admin/auth/forgot-password`,
      data
    );
    return response.data;
  }

  async validateResetToken(token: string): Promise<BffApiResponse<ValidateResetTokenResponse>> {
    const response = await api.get<BffApiResponse<ValidateResetTokenResponse>>(
      `/admin/auth/validate-reset-token?token=${encodeURIComponent(token)}`
    );
    return response.data;
  }

  async resetPasswordWithToken(data: ResetPasswordRequest): Promise<BffApiResponse<null>> {
    const response = await api.post<BffApiResponse<null>>(
      `/admin/auth/reset-password`,
      data
    );
    return response.data;
  }
}

export const adminUsersService = new AdminUsersService();
export default adminUsersService;